import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  TablePagination
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import VisibilityIcon from '@mui/icons-material/Visibility'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import SearchIcon from '@mui/icons-material/Search'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import Tooltip from '@mui/material/Tooltip'
import axios from 'axios'
import ViewDocument from '../../../../../shared-ui/ViewDocument/ViewDocument'
import { crudApiInstance } from '../../../../../lib/requests.es6'
import {
  hasAuthourity,
  getAuthorizationToken
} from '../../../../../lib/access.es6'

function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cancellation-tabpanel-${index}`}
      aria-labelledby={`cancellation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function CancellationStatusChip({ status }) {
  const statusConfig = {
    AwaitingApproval: { label: 'Awaiting Approval', color: 'warning' },
    Approved: { label: 'Approved', color: 'success' },
    Rejected: { label: 'Rejected', color: 'error' },
    Executed: { label: 'Executed', color: 'info' }
  }
  const config = statusConfig[status] || { label: status, color: 'default' }
  return <Chip label={config.label} color={config.color} size="small" />
}

function CancellationDetailDialog({
  item,
  open,
  onClose,
  onApprove,
  onReject,
  loading,
  error,
  onClearError
}) {
  const [decisionReason, setDecisionReason] = useState('')
  const [policyData, setPolicyData] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [policyLoading, setPolicyLoading] = useState(false)
  const [showRejectReason, setShowRejectReason] = useState(false)
  const [viewDocumentUrl, setViewDocumentUrl] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    if (!open) {
      setDecisionReason('')
      onClearError()
      setPolicyData(null)
      setDetailData(null)
      setShowRejectReason(false)
    } else if (item && item.guid) {
      fetchCancellationDetail(item.guid)
      if (item.policy_number) {
        fetchPolicyDetails(item.policy_number)
      }
    }
  }, [open, onClearError, item])

  const handleApprove = () => {
    onApprove(item.guid, decisionReason)
    setDecisionReason('')
    setShowRejectReason(false)
  }

  const handleReject = () => {
    if (showRejectReason) {
      onReject(item.guid, decisionReason)
      setDecisionReason('')
      setShowRejectReason(false)
    } else {
      setShowRejectReason(true)
    }
  }

  const fetchCancellationDetail = async guid => {
    try {
      setDetailLoading(true)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      const response = await crudApiInstance.get(
        `api/policies/complex/cancellations/${encodeURIComponent(guid)}`
      )
      setDetailData(response.data)
    } catch (err) {
      console.error('Error fetching cancellation detail:', err)
    } finally {
      setDetailLoading(false)
    }
  }

  const fetchPolicyDetails = async policyNumber => {
    try {
      setPolicyLoading(true)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      const response = await crudApiInstance.get(
        `api/policies/complex/policy_number?policy_number=${encodeURIComponent(
          policyNumber
        )}`
      )
      setPolicyData(response.data)
    } catch (err) {
      console.error('Error fetching policy details:', err)
    } finally {
      setPolicyLoading(false)
    }
  }

  if (!item) return null

  const displayItem = detailData ? { ...item, ...detailData } : item
  const currentStatus = displayItem.status || displayItem.cancellation_status
  const isAwaitingApproval = currentStatus === 'AwaitingApproval'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancellation Request Details</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
          >
            <Tab label="Request Details" />
            <Tab label="Policy Details" />
            <Tab label="Customer Details" />
          </Tabs>
        </Box>

        <Stack spacing={3}>
          {/* Tab 1: Request Details Section */}
          {tabValue === 0 && (
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Policy Number
                      </TableCell>
                      <TableCell>{displayItem.policy_number}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Current Status
                      </TableCell>
                      <TableCell>
                        <CancellationStatusChip status={currentStatus} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Initiated By
                      </TableCell>
                      <TableCell>{displayItem.initiated_by_name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Effective Date
                      </TableCell>
                      <TableCell>
                        {displayItem.effective_date ||
                          displayItem.effective_cancellation_date ||
                          '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        Reason
                      </TableCell>
                      <TableCell>
                        {displayItem.reason || displayItem.request_reason || '—'}
                      </TableCell>
                    </TableRow>
                    {displayItem.additional_details && displayItem.additional_details.notes && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                          Additional Notes
                        </TableCell>
                        <TableCell>
                          {displayItem.additional_details.notes}
                        </TableCell>
                      </TableRow>
                    )}
                    {(currentStatus === 'Approved' ||
                      currentStatus === 'Rejected' ||
                      currentStatus === 'Executed') && (
                      <>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Decision Maker
                          </TableCell>
                          <TableCell>{displayItem.approved_by_name || '—'}</TableCell>
                        </TableRow>
                        {displayItem.approved_at && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                              Approval Time
                            </TableCell>
                            <TableCell>
                              {new Date(displayItem.approved_at).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        )}
                        {currentStatus === 'Rejected' && displayItem.rejection_reason && (
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 'bold',
                                width: '40%'
                              }}
                            >
                              Rejection Reason
                            </TableCell>
                            <TableCell>{displayItem.rejection_reason}</TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Supporting Document Section */}
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 'bold' }}
                >
                  Supporting Document
                </Typography>
                {detailLoading || policyLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading document...</Typography>
                  </Box>
                ) : (
                  (() => {
                    const cancellationDoc =
                      policyData && policyData.policy_onboarding_documents
                        ? policyData.policy_onboarding_documents.find(
                            doc =>
                              doc.description ===
                              'Cancellation Supporting Document'
                          )
                        : null
                    return cancellationDoc ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          backgroundColor: '#f9f9f9',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            backgroundColor: '#1a3a52',
                            borderRadius: 1,
                            color: 'white',
                            flexShrink: 0
                          }}
                        >
                          <InsertDriveFileOutlinedIcon
                            sx={{ color: '#ffffff' }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cancellationDoc.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(
                              cancellationDoc.created_at
                            ).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Tooltip title="View Document">
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (cancellationDoc.url) {
                                setViewDocumentUrl(cancellationDoc.url)
                              }
                            }}
                          >
                            <VisibilityIcon sx={{ color: '#4B7E34' }} />
                          </span>
                        </Tooltip>
                        <Tooltip title="Download Document">
                          <span
                            style={{ cursor: 'pointer', marginLeft: '10px' }}
                            onClick={() => {
                              if (cancellationDoc.url) {
                                const a = document.createElement('a')
                                a.href = cancellationDoc.url
                                a.download = cancellationDoc.description
                                document.body.appendChild(a)
                                a.click()
                                document.body.removeChild(a)
                              }
                            }}
                          >
                            <FileDownloadIcon sx={{ color: '#000' }} />
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No supporting document attached
                      </Typography>
                    )
                  })()
                )}
              </Box>
            </Box>
          )}

          {/* Tab 2: Policy Details Section */}
          {tabValue === 1 && (
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {policyLoading ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                              Loading policy details...
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : policyData ? (
                      <>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Product
                          </TableCell>
                          <TableCell>
                            {policyData.product_name || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Status
                          </TableCell>
                          <TableCell>{policyData.status || '—'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Start Date
                          </TableCell>
                          <TableCell>
                            {policyData.start_date
                              ? new Date(
                                  policyData.start_date
                                ).toLocaleDateString()
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Due Date
                          </TableCell>
                          <TableCell>
                            {policyData.due_date
                              ? new Date(
                                  policyData.due_date
                                ).toLocaleDateString()
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Payment Method
                          </TableCell>
                          <TableCell>
                            {policyData.payment_method || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Currency
                          </TableCell>
                          <TableCell>{policyData.currency || '—'}</TableCell>
                        </TableRow>
                        {policyData.quotation && (
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                                Premium Amount
                              </TableCell>
                              <TableCell>
                                {policyData.quotation.total_premium_amount_in_cents
                                  ? `${(policyData.quotation.total_premium_amount_in_cents / 100).toLocaleString()} ${policyData.currency || ''}`
                                  : '—'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                                Premium Frequency
                              </TableCell>
                              <TableCell>
                                {policyData.quotation.premium_cardinality &&
                                policyData.quotation.premium_granularity
                                  ? `Every ${policyData.quotation.premium_cardinality} ${policyData.quotation.premium_granularity}`
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Total Paid Amount
                          </TableCell>
                          <TableCell>
                            {policyData.total_paid_amount_in_cents !== undefined
                              ? `${(policyData.total_paid_amount_in_cents / 100).toLocaleString()} ${policyData.currency || ''}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Balance/Refund Amount
                          </TableCell>
                          <TableCell>
                            {policyData.total_payable_amount !== undefined
                              ? `${(policyData.total_payable_amount / 100).toLocaleString()} ${policyData.currency || ''}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="textSecondary">
                            No policy details available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 3: Customer Details Section */}
          {tabValue === 2 && (
            <Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {policyLoading ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                              Loading customer details...
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : policyData && policyData.additional_details ? (
                      <>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Full Name
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.first_name}{' '}
                            {policyData.additional_details.last_name || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Email
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.email || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Phone
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.phone_number || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            National ID
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.national_id || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Agent Name
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.agent_name || '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Payment Frequency
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.payment_frequency ||
                              '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                            Payment Day
                          </TableCell>
                          <TableCell>
                            {policyData.additional_details.payment_day || '—'}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="textSecondary">
                            No customer details available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {isAwaitingApproval && showRejectReason && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason"
              placeholder="Enter reason for rejection..."
              value={decisionReason}
              onChange={e => setDecisionReason(e.target.value)}
            />
          )}
        </Stack>

        <ViewDocument
          title="Supporting Document"
          open={viewDocumentUrl}
          close={() => setViewDocumentUrl(null)}
          viewDocumentUrl={viewDocumentUrl}
          numPages={numPages}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        {isAwaitingApproval && (
          <>
            <Button
              onClick={handleReject}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {showRejectReason ? 'Confirm Rejection' : 'Reject'}
            </Button>
            <Button
              onClick={handleApprove}
              color="success"
              variant="contained"
              disabled={loading}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

function CancellationTable({
  items,
  title,
  onViewItem,
  loading,
  onSearch,
  searchQuery,
  onSearchChange,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}) {
  const handleSearch = () => {
    if (onSearch) {
      onSearch()
    }
  }

  if (loading) {
    return (
      <Box>
        <Box
          sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}
        >
          <TextField
            size="small"
            placeholder="Search by policy number..."
            value={searchQuery}
            onChange={onSearchChange}
            disabled
            sx={{ width: 280 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled
          >
            Search
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Policy Number</TableCell>
                <TableCell>Initiated By</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 20,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  if (!items || items.length === 0) {
    return (
      <Box>
        <Box
          sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}
        >
          <TextField
            size="small"
            placeholder="Search by policy number..."
            value={searchQuery}
            onChange={onSearchChange}
            sx={{ width: 280 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            No {title.toLowerCase()} items
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <TextField
          size="small"
          placeholder="Search by policy number..."
          value={searchQuery}
          onChange={onSearchChange}
          sx={{ width: 280 }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Policy Number</TableCell>
              <TableCell>Initiated By</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.guid}>
                <TableCell>{item.policy_number}</TableCell>
                <TableCell>{item.initiated_by_name}</TableCell>
                <TableCell>
                  {item.effective_date || item.effective_cancellation_date}
                </TableCell>
                <TableCell>{item.reason || item.request_reason}</TableCell>
                <TableCell>
                  <CancellationStatusChip
                    status={item.status || item.cancellation_status}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onViewItem(item)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={(event, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={event =>
            onPageSizeChange(parseInt(event.target.value, 10))
          }
        />
      </TableContainer>
    </Box>
  )
}

function PolicyCancellationDashboard({ partner, hasApprovalRole }) {
  const [tabValue, setTabValue] = useState(0)
  const [statsLoading, setStatsLoading] = useState(true)
  const [tabLoading, setTabLoading] = useState({
    awaiting_approval: false,
    approved: false,
    rejected: false,
    executed: false
  })
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState(null)
  const [dialogError, setDialogError] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [stats, setStats] = useState({
    pending_approval: 0,
    approved: 0,
    rejected: 0,
    executed: 0
  })
  const [tabData, setTabData] = useState({
    awaiting_approval: [],
    approved: [],
    rejected: [],
    executed: []
  })
  const [pagination, setPagination] = useState({
    awaiting_approval: { page: 0, pageSize: 10, totalCount: 0 },
    approved: { page: 0, pageSize: 10, totalCount: 0 },
    rejected: { page: 0, pageSize: 10, totalCount: 0 },
    executed: { page: 0, pageSize: 10, totalCount: 0 }
  })
  const [searchQueries, setSearchQueries] = useState({
    awaiting_approval: '',
    approved: '',
    rejected: '',
    executed: ''
  })

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData, partner])

  const fetchStatsOnly = async () => {
    try {
      setStatsLoading(true)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      const response = await crudApiInstance.get(
        `api/policies/complex/cancellations/dashboard`
      )

      const data = response.data || {}
      const statsData = data.stats || {}
      setStats({
        pending_approval: statsData.pending_approval || 0,
        approved: statsData.approved || 0,
        rejected: statsData.rejected || 0,
        executed: statsData.executed || 0
      })
    } catch (err) {
      console.error('[CancellationDashboard] Error loading stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  const fetchTabData = async (
    status,
    searchQuery = '',
    pageNum = 0,
    pageSize = 10
  ) => {
    try {
      setError(null)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      const statusKey = status
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')

      setTabLoading(prev => ({
        ...prev,
        [statusKey]: true
      }))

      const params = {
        status,
        skip: pageNum * pageSize,
        take: pageSize
      }

      if (searchQuery) {
        params.policy_number = searchQuery
      }

      const response = await crudApiInstance.get(
        `api/policies/complex/cancellations`,
        { params }
      )

      const items = response.data.items || []
      const totalCount = response.data.totalCount || response.data.total || 0

      setTabData(prev => ({
        ...prev,
        [statusKey]: items
      }))

      setPagination(prev => ({
        ...prev,
        [statusKey]: { page: pageNum, pageSize, totalCount }
      }))
    } catch (err) {
      const errorMessage =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to load cancellations'
      setError(errorMessage)
      console.error('[CancellationDashboard] Error loading:', err.message)
    } finally {
      const statusKey = status
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')
      setTabLoading(prev => ({
        ...prev,
        [statusKey]: false
      }))
    }
  }

  const fetchDashboardData = useCallback(async () => {
    await fetchStatsOnly()
    const tabs = ['AwaitingApproval', 'Approved', 'Rejected', 'Executed']
    for (const status of tabs) {
      await fetchTabData(status, '', 0, 10)
    }
  }, [])

  const handleApproveCancellation = async cancellationGuid => {
    try {
      setActionLoading(true)
      setDialogError(null)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      await crudApiInstance.post(
        `api/policies/complex/cancellations/${encodeURIComponent(
          cancellationGuid
        )}/approve`,
        { decision: 'approve' }
      )
      setDetailDialogOpen(false)
      setSelectedItem(null)
      await fetchDashboardData()
    } catch (err) {
      const errorMessage =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to approve cancellation'
      setDialogError(errorMessage)
      console.error('Error approving cancellation:', err)
    } finally {
      setActionLoading(false)
    }
  }

  const handleRejectCancellation = async (cancellationGuid, reason) => {
    try {
      setActionLoading(true)
      setDialogError(null)
      axios.defaults.headers.common.Authorization = `Bearer ${getAuthorizationToken()}`
      await crudApiInstance.post(
        `api/policies/complex/cancellations/${encodeURIComponent(
          cancellationGuid
        )}/reject`,
        { decision: 'reject', rejection_reason: reason }
      )
      setDetailDialogOpen(false)
      setSelectedItem(null)
      await fetchDashboardData()
    } catch (err) {
      const errorMessage =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Failed to reject cancellation'
      setDialogError(errorMessage)
      console.error('Error rejecting cancellation:', err)
    } finally {
      setActionLoading(false)
    }
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  const tabs = [
    {
      label: 'Awaiting Approval',
      key: 'awaiting_approval',
      items: tabData.awaiting_approval
    },
    { label: 'Approved', key: 'approved', items: tabData.approved },
    { label: 'Rejected', key: 'rejected', items: tabData.rejected },
    { label: 'Executed', key: 'executed', items: tabData.executed }
  ]

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
    const statusMap = {
      0: 'AwaitingApproval',
      1: 'Approved',
      2: 'Rejected',
      3: 'Executed'
    }
    const status = statusMap[newValue]
    const statusKey = status
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '')
    const searchQuery = searchQueries[statusKey]
    const paginationState = pagination[statusKey]
    fetchTabData(
      status,
      searchQuery,
      paginationState.page,
      paginationState.pageSize
    )
  }

  const handleSearch = tabKey => {
    const statusMap = {
      awaiting_approval: 'AwaitingApproval',
      approved: 'Approved',
      rejected: 'Rejected',
      executed: 'Executed'
    }
    const status = statusMap[tabKey]
    fetchTabData(status, searchQueries[tabKey], 0, pagination[tabKey].pageSize)
  }

  const handlePageChange = (tabKey, newPage) => {
    const statusMap = {
      awaiting_approval: 'AwaitingApproval',
      approved: 'Approved',
      rejected: 'Rejected',
      executed: 'Executed'
    }
    const status = statusMap[tabKey]
    fetchTabData(
      status,
      searchQueries[tabKey],
      newPage,
      pagination[tabKey].pageSize
    )
  }

  const handlePageSizeChange = (tabKey, newPageSize) => {
    const statusMap = {
      awaiting_approval: 'AwaitingApproval',
      approved: 'Approved',
      rejected: 'Rejected',
      executed: 'Executed'
    }
    const status = statusMap[tabKey]
    fetchTabData(status, searchQueries[tabKey], 0, newPageSize)
  }

  const handleSearchQueryChange = (tabKey, value) => {
    setSearchQueries(prev => ({
      ...prev,
      [tabKey]: value
    }))
  }

  if (!hasApprovalRole) {
    return (
      <Alert severity="error">
        You do not have permission to access the Policy Cancellation Dashboard.
        Required role: OMM_POLICY_CANCELLATION_APPROVAL
      </Alert>
    )
  }

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        Policy Cancellation Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {statsLoading ? (
                <>
                  <Box
                    sx={{
                      height: 16,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <Box
                    sx={{
                      height: 32,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 32, color: '#ff9800' }} />
                  </Box>
                  <Typography color="textSecondary" gutterBottom>
                    Awaiting Approval
                  </Typography>
                  <Typography variant="h6">{stats.pending_approval}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {statsLoading ? (
                <>
                  <Box
                    sx={{
                      height: 16,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <Box
                    sx={{
                      height: 32,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 32, color: '#4caf50' }} />
                  </Box>
                  <Typography color="textSecondary" gutterBottom>
                    Approved
                  </Typography>
                  <Typography variant="h6">{stats.approved}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {statsLoading ? (
                <>
                  <Box
                    sx={{
                      height: 16,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <Box
                    sx={{
                      height: 32,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <BlockIcon sx={{ fontSize: 32, color: '#f44336' }} />
                  </Box>
                  <Typography color="textSecondary" gutterBottom>
                    Rejected
                  </Typography>
                  <Typography variant="h6">{stats.rejected}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              {statsLoading ? (
                <>
                  <Box
                    sx={{
                      height: 16,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <Box
                    sx={{
                      height: 32,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 1
                    }}
                  />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <TaskAltIcon sx={{ fontSize: 32, color: '#2196f3' }} />
                  </Box>
                  <Typography color="textSecondary" gutterBottom>
                    Executed
                  </Typography>
                  <Typography variant="h6">{stats.executed}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          {tabs.map((tab, idx) => (
            <Tab key={idx} label={`${tab.label} (${tab.items.length})`} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabs.map((tab, idx) => (
        <TabPanel key={idx} value={tabValue} index={idx}>
          <CancellationTable
            items={tab.items}
            title={tab.label}
            loading={tabLoading[tab.key]}
            onViewItem={item => {
              setSelectedItem(item)
              setDetailDialogOpen(true)
            }}
            searchQuery={searchQueries[tab.key]}
            onSearchChange={e =>
              handleSearchQueryChange(tab.key, e.target.value)
            }
            onSearch={() => handleSearch(tab.key)}
            page={pagination[tab.key].page}
            pageSize={pagination[tab.key].pageSize}
            totalCount={pagination[tab.key].totalCount}
            onPageChange={newPage => handlePageChange(tab.key, newPage)}
            onPageSizeChange={newPageSize =>
              handlePageSizeChange(tab.key, newPageSize)
            }
          />
        </TabPanel>
      ))}

      {/* Detail Dialog */}
      <CancellationDetailDialog
        item={selectedItem}
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false)
          setSelectedItem(null)
        }}
        onApprove={handleApproveCancellation}
        onReject={handleRejectCancellation}
        loading={actionLoading}
        error={dialogError}
        onClearError={() => setDialogError(null)}
      />

      {/* Refresh Button */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography
          variant="body2"
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            display: 'inline-block'
          }}
          onClick={fetchDashboardData}
        >
          ↻ Refresh
        </Typography>
      </Box>
    </Box>
  )
}

const mapStateToProps = state => ({
  partner:
    state && state.user && state.user.partner_guid
      ? state.user.partner_guid
      : 'old_mutual_malawi',
  hasApprovalRole: hasAuthourity('OMM_POLICY_CANCELLATION_APPROVAL')
})

export default connect(mapStateToProps)(PolicyCancellationDashboard)
